import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

const AgentProfile = () => {
  const { customAgentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user] = useState(JSON.parse(localStorage.getItem('user')));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // Determine the agent ID to use
        const agentId = user.role === 'admin' ? customAgentId : user.customAgentId;
        if (!agentId) {
          throw new Error('Agent ID not found');
        }

        // Fetch agent details
        const agentRes = await axios.get(`${config.apiBaseUrl}/api/agents/custom/${agentId}`);
        setAgent(agentRes.data);
      } catch (err) {
        console.error('Error fetching agent:', err);
        setError(err.response?.data?.message || 'Failed to fetch agent details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customAgentId, user]);

  const handleCreateReport = () => {
    navigate(`/agents/${agent.customAgentId}/call-reports/new`);
  };

  const handleViewReports = () => {
    navigate(`/agents/${agent.customAgentId}/call-reports`);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!agent) {
    return (
      <Container>
        <Alert severity="error">Agent not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        {user.role === 'admin' && (
          <Button
            startIcon={<Edit />}
            onClick={() => navigate(`/agents/${agent._id}/edit`)}
            variant="outlined"
            color="primary"
          >
            Edit Agent
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {agent.name}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1">{agent.email}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" color="text.secondary">
              Phone
            </Typography>
            <Typography variant="body1">{agent.phone}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary">
              To Email Addresses
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {agent.to && agent.to.length > 0 ? (
                agent.to.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No email addresses
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary">
              CC Email Addresses
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {agent.cc && agent.cc.length > 0 ? (
                agent.cc.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No email addresses
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewReports}
              >
                View Call Reports
              </Button>
              {user.role === 'admin' && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCreateReport}
                >
                  Create New Report
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default AgentProfile; 